/**
 * OX, kept as its own module for the names the rest of the app already uses.
 *
 * The rules themselves moved to `grid.ts` the day a second game arrived: three
 * in a row on three by three is the same question as four in a row on seven by
 * six, and answering it twice is two chances to miss a diagonal. What stays
 * here is the vocabulary — `LINES`, `winningLine`, `applyMove` — so nothing
 * that already imported them had to change.
 */
import { TICTACTOE } from './rules.ts';
import { cellAt, type Grid } from './grid.ts';

export type { Mark } from './grid.ts';
export { EMPTY_CELL } from './grid.ts';

export const EMPTY_BOARD = TICTACTOE.emptyBoard;
export const BOARD_SIZE = 9;

const GRID: Grid = TICTACTOE.grid;

/**
 * The eight ways to win: three rows, three columns, two diagonals.
 *
 * Built from the grid rather than typed out, so it cannot disagree with the
 * scanner that actually decides a game.
 */
export const LINES: ReadonlyArray<readonly [number, number, number]> = (() => {
  const lines: [number, number, number][] = [];
  for (let row = 0; row < 3; row += 1) {
    lines.push([cellAt(0, row, GRID), cellAt(1, row, GRID), cellAt(2, row, GRID)]);
  }
  for (let column = 0; column < 3; column += 1) {
    lines.push([cellAt(column, 0, GRID), cellAt(column, 1, GRID), cellAt(column, 2, GRID)]);
  }
  lines.push([cellAt(0, 0, GRID), cellAt(1, 1, GRID), cellAt(2, 2, GRID)]);
  lines.push([cellAt(2, 0, GRID), cellAt(1, 1, GRID), cellAt(0, 2, GRID)]);
  return lines;
})();

export const isBoard = (board: string): boolean => TICTACTOE.isBoard(board);
export const turnOf = (board: string) => TICTACTOE.turnOf(board);
export const winnerOf = (board: string) => TICTACTOE.winnerOf(board);
export const isDraw = (board: string): boolean => TICTACTOE.isDraw(board);
export const isOver = (board: string): boolean => TICTACTOE.isOver(board);

/** The winning line, or null. Returns the cells so the UI can draw through them. */
export function winningLine(board: string): readonly [number, number, number] | null {
  const run = TICTACTOE.winningRun(board);
  return run && run.length === 3 ? ([run[0], run[1], run[2]] as [number, number, number]) : null;
}

export const isLegalMove = (board: string, cell: number, mark: 'X' | 'O'): boolean =>
  TICTACTOE.isLegalMove(board, cell, mark);

export const applyMove = (board: string, cell: number, mark: 'X' | 'O'): string | null =>
  TICTACTOE.applyMove(board, cell, mark);
